import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  styled,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { resetPassword } from "@services/auth";
import { useNavigate, useLocation } from "react-router-dom";
import ContentUsBgImage from "@assets/ContentUsBgImage.png";
const SignUpContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#ffffff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  padding: "16px",
  [theme.breakpoints.down("sm")]: {
    padding: "8px",
    alignItems: "flex-start",
    paddingTop: "20px",
  },
}));

const ContactSection = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px 0px",
  position: "relative",
  width: "100%",
  maxWidth: "900px",
  margin: "0 auto",
  gap: 16,
  [theme.breakpoints.down("lg")]: {
    gap: 12,
    flexDirection: "column",
    padding: "12px",
    maxWidth: "900px",
  },
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    padding: "16px 8px",
    gap: 10,
    maxWidth: "100%",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "12px 6px",
    gap: 8,
  },
}));

const BackgroundOverlay = styled(Box)({
  position: "absolute",
  height: "100%",
  left: 0,
  top: 0,
  width: "100%",
  backgroundImage: `
    radial-gradient(ellipse 42.02px 68.593px at 298.8px 124.35px, rgba(220,253,255,0.4) 0%, rgba(255,255,255,0) 100%),
    radial-gradient(ellipse 77.28px 6.0674px at 1471.2px 363.44px, rgba(220,253,255,0.4) 0%, rgba(255,255,255,0) 100%),
    radial-gradient(ellipse 1.62px 57.671px at 682.2px 879.48px, rgba(197,255,225,0.4) 0%, rgba(255,255,255,0) 100%)
  `,
});
const LeftPanel = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "620px",
  height: "520px",
  backgroundImage: `url(${ContentUsBgImage})`,
  borderRadius: "32px",
  border: "1px solid #ffffff",
  overflow: "hidden",
  marginRight: "-80px",
  backgroundSize: "cover",
  backgroundRepeat: "no-repeat",
  flexShrink: 0,
  [theme.breakpoints.down("lg")]: {
    width: "480px",
    height: "400px",
    marginRight: "-20px",
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    maxWidth: "500px",
    height: "auto",
    minHeight: "240px",
    aspectRatio: "16 / 10",
    marginRight: 0,
    borderRadius: "24px",
  },
  [theme.breakpoints.down("sm")]: {
    maxWidth: "100%",
    minHeight: "240px",
    borderRadius: "16px",
  },
}));

const LeftPanelContent = styled(Box)(({ theme }) => ({
  position: "absolute",
  left: "95.5px",
  top: "101px",
  width: "470px",
  color: "#ffffff",
  [theme.breakpoints.down("lg")]: {
    left: "32px",
    top: "48px",
    width: "380px",
  },
  [theme.breakpoints.down("md")]: {
    position: "relative",
    left: 0,
    top: 0,
    width: "100%",
    padding: "16px 12px 12px",
    textAlign: "center",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "16px 12px 12px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const MainTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "64px",
  lineHeight: "1.1",
  marginBottom: "40px",
  color: "#ffffff",
  [theme.breakpoints.down("lg")]: {
    fontSize: "42px",
    marginBottom: "24px",
  },
  [theme.breakpoints.down("md")]: {
    fontSize: "36px",
    marginBottom: "12px",
    lineHeight: "1.2",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "28px",
    marginBottom: "12px",
    paddingTop: "48px",
    textAlign: "center",
  },
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 500,
  fontSize: "18px",
  lineHeight: "1.5",
  width: "422px",
  color: "#ffffff",
  textAlign: "center",
  whiteSpace: "normal",
  overflowWrap: "normal",
  wordBreak: "normal",
  hyphens: "none",
  [theme.breakpoints.down("lg")]: {
    width: "360px",
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    fontSize: "16px",
    lineHeight: "1.45",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
    lineHeight: "1.4",
    maxWidth: 300,
    margin: "0 auto",
    textAlign: "center",
    whiteSpace: "normal",
    overflowWrap: "normal",
    wordBreak: "normal",
    hyphens: "none",
  },
}));

const RightPanel = styled(Box)(({ theme }) => ({
  backgroundColor: "#f4feff",
  border: "1px solid #05495a",
  borderRadius: "16px",
  padding: "32px 28px 32px 32px",
  width: "480px",
  position: "relative",
  flexShrink: 0,
  minHeight: 500,
  [theme.breakpoints.down("lg")]: {
    width: "420px",
    padding: "28px 24px 28px 28px",
    minHeight: 540,
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    maxWidth: "480px",
    padding: "24px 18px 24px 24px",
    minHeight: 520,
  },
  [theme.breakpoints.down("sm")]: {
    padding: "20px 14px 20px 20px",
    borderRadius: "12px",
    margin: "0 6px",
    maxWidth: "90%",
    minHeight: 500,
  },
}));

const FormContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "32px",
  width: "100%",
  height: "100%",
});

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "32px",
  alignItems: "flex-start",
  [theme.breakpoints.down("sm")]: {
    gap: "10px",
    alignItems: "center",
  },
}));

const BackButton = styled(Button)({
  minWidth: "32px",
  width: "32px",
  height: "32px",
  padding: 0,
  color: "#033843",
  "&:hover": {
    backgroundColor: "rgba(3, 56, 67, 0.04)",
  },
});

const PageTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 700,
  fontSize: "28px",
  lineHeight: "35px",
  color: "#033843",
  height: "35px",
  //textAlign: "center", 
  flex: 1,
  whiteSpace: "nowrap",
  [theme.breakpoints.down("sm")]: {
    fontSize: "24px",
    lineHeight: "30px",
  },
}));

const PageSubtitle = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 500,
  fontSize: "16px",
  // lineHeight: "1.5",
  color: "#262626",
  width: "100%",
  mt: "0px",
});

const InputContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "4.5px",
  width: "100%",
});

const InputLabel = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  lineHeight: "20px",
  color: "#262626",
});

const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",
    height: "42px",
    borderRadius: "4px",
    "& fieldset": {
      borderColor: "#e6fafc",
    },
    "&:hover fieldset": {
      borderColor: "#bfe5ec",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#0a829d",
    },
    boxShadow:
      "inset 0px 0px 8px 0px rgba(0,108,116,0.12), inset 0px 0px 4px 0px rgba(34,110,127,0.25)",
  },
  "& .MuiOutlinedInput-input": {
    padding: "11px 14px",
    fontSize: "14px",
    fontFamily: "Rethink Sans, sans-serif",
  },
});

const PasswordStrengthContainer = styled(Box)({
  display: "flex",
  gap: "2px",
  alignItems: "center",
  padding: "2px",
  width: "100%",
});

const PasswordStrengthBar = styled(Box)<{ isActive: boolean }>(
  ({ isActive }) => ({
    flex: 1,
    height: "4px",
    backgroundColor: isActive ? "#0a829d" : "#e5e7eb",
    borderRadius: "16px",
  })
);

const FormContent = styled(Box)({
  display: "flex",
  flexDirection: "column",
  // gap: "35px",
  alignItems: "center",
  // padding: "24px 0",
  flex: 1,
  width: "100%",
});

const PasswordSection = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  width: "100%",
});

const SingleInputSection = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  width: "100%",
});

const ContinueButton = styled(Button)<{ isActive: boolean }>(
  ({ isActive }) => ({
    background: isActive
      ? "linear-gradient(90deg, #0a829d 0%, #35d7e4 100%)"
      : "#ddf0f4",
    border: "1px solid #ffffff",
    borderRadius: "60px",
    height: "60px",
    width: "100%",
    padding: "12px 24px",
    textTransform: "none",
    fontFamily: "Rethink Sans, sans-serif",
    fontWeight: 600,
    fontSize: "18px",
    lineHeight: "22px",
    color: isActive ? "#ffffff" : "#bad6dc",
    boxShadow: isActive
      ? "0px 4px 24px 0px rgba(10,130,157,0.2), 0px 4px 7.3px 0px rgba(133,233,255,0.4)"
      : "none",
    "&:hover": {
      background: isActive
        ? "linear-gradient(90deg, #086e86 0%, #2bc9d6 100%)"
        : "#ddf0f4",
    },
    "&:disabled": {
      background: "#ddf0f4",
      color: "#bad6dc",
    },
  })
);

export default function ResetPassword() {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const email = (location.state as { email?: string } | null)?.email || "";

  useEffect(() => {
    if (!email) {
      navigate("/forgot-password");
      return;
    }
  }, [email, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    navigate("/verify-otp", { state: { email } });
  };

  const validatePassword = (password: string) => {
    const minLength = password.length >= 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    const score = [
      minLength,
      hasUppercase,
      hasLowercase,
      hasNumbers,
      hasSpecialChar,
    ].filter(Boolean).length;
    return {
      score,
      minLength,
      hasUppercase,
      hasLowercase,
      hasNumbers,
      hasSpecialChar,
    };
  };

  const passwordValidation = validatePassword(newPassword);
  const isPasswordValid =
    passwordValidation.minLength && passwordValidation.score >= 4;
  const doPasswordsMatch =
    newPassword === confirmPassword && confirmPassword !== "";
  const isFormValid = isPasswordValid && doPasswordsMatch;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !isFormValid) return;

    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const response = await resetPassword(email, newPassword);
      if (response.success) {
        setSuccess("Password has been reset successfully!");
        setTimeout(() => {
          navigate("/reset-password-success");
        }, 1500);
      } else {
        setError(response.message || "Password reset failed");
      }
    } catch (err: any) {
      let errorMessage = "Password reset failed. Please try again.";
      try {
        const parsed = JSON.parse(err.message);
        errorMessage = parsed?.detail || parsed?.message || errorMessage;
      } catch {
        errorMessage = err.message || errorMessage;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!email) {
    return null;
  }

  return (
    <SignUpContainer>
      <ContactSection>
        <BackgroundOverlay />

        <LeftPanel>
          <LeftPanelContent>
            <MainTitle>Welcome Back to UniComply</MainTitle>
            <SubTitle>
              Stay connected with clinics nationwide ensuring better healthcare
              compliance.
            </SubTitle>
          </LeftPanelContent>
        </LeftPanel>

        <RightPanel>
          <FormContainer>
            <HeaderContainer>
              <BackButton onClick={handleBack}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"
                    fill="currentColor"
                  />
                </svg>
              </BackButton>
              <PageTitle>Set New Password</PageTitle>
            </HeaderContainer>

            <PageSubtitle>
              Password must meet the following requirements:
            </PageSubtitle>
            <Box component="ul" sx={{ mb: 0, mt: 0 }}>
              <Typography
                component="li"
                sx={{ fontSize: "14px", color: "#555" }}
              >
                Minimum 8 characters
              </Typography>
              <Typography
                component="li"
                sx={{ fontSize: "14px", color: "#555" }}
              >
                At least one uppercase letter (A–Z)
              </Typography>
              <Typography
                component="li"
                sx={{ fontSize: "14px", color: "#555" }}
              >
                At least one lowercase letter (a–z)
              </Typography>
              <Typography
                component="li"
                sx={{ fontSize: "14px", color: "#555" }}
              >
                At least one number (0–9)
              </Typography>
              <Typography
                component="li"
                sx={{ fontSize: "14px", color: "#555" }}
              >
                At least one special character (e.g., !@#$%^&*)
              </Typography>
            </Box>
            <FormContent>
              <Box
                component="form"
                onSubmit={onSubmit}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "15px",
                  width: "100%",
                }}
              >
                <PasswordSection>
                  <SingleInputSection>
                    <InputContainer>
                      <InputLabel>Enter New Password</InputLabel>
                      <StyledTextField
                        fullWidth
                        type={showNewPassword ? "text" : "password"}
                        variant="outlined"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="*********"
                        required
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() =>
                                  setShowNewPassword(!showNewPassword)
                                }
                                edge="end"
                                size="small"
                                sx={{ color: "#9ca3af" }}
                              >
                                {showNewPassword ? (
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M12 7C14.7614 7 17 9.23858 17 12C17 14.7614 14.7614 17 12 17C9.23858 17 7 14.7614 7 12C7 9.23858 9.23858 7 12 7Z"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                    />
                                    <path
                                      d="M12 1C18.3939 1 23.7279 5.02369 24 12C23.7279 18.9763 18.3939 23 12 23C5.60607 23 0.272126 18.9763 0 12C0.272126 5.02369 5.60607 1 12 1Z"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                    />
                                  </svg>
                                ) : (
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                    />
                                    <circle
                                      cx="12"
                                      cy="12"
                                      r="3"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                    />
                                  </svg>
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </InputContainer>

                    <PasswordStrengthContainer>
                      <PasswordStrengthBar
                        isActive={passwordValidation.score >= 1}
                      />
                      <PasswordStrengthBar
                        isActive={passwordValidation.score >= 2}
                      />
                      <PasswordStrengthBar
                        isActive={passwordValidation.score >= 3}
                      />
                      <PasswordStrengthBar
                        isActive={passwordValidation.score >= 4}
                      />
                      <PasswordStrengthBar
                        isActive={passwordValidation.score >= 5}
                      />
                    </PasswordStrengthContainer>
                  </SingleInputSection>

                  <InputContainer>
                    <InputLabel>Confirm New Password</InputLabel>
                    <StyledTextField
                      fullWidth
                      type={showConfirmPassword ? "text" : "password"}
                      variant="outlined"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="*********"
                      required
                      error={confirmPassword !== "" && !doPasswordsMatch}
                      helperText={
                        confirmPassword !== "" && !doPasswordsMatch
                          ? "Passwords do not match"
                          : ""
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              edge="end"
                              size="small"
                              sx={{ color: "#9ca3af" }}
                            >
                              {showConfirmPassword ? (
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M12 7C14.7614 7 17 9.23858 17 12C17 14.7614 14.7614 17 12 17C9.23858 17 7 14.7614 7 12C7 9.23858 9.23858 7 12 7Z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  />
                                  <path
                                    d="M12 1C18.3939 1 23.7279 5.02369 24 12C23.7279 18.9763 18.3939 23 12 23C5.60607 23 0.272126 18.9763 0 12C0.272126 5.02369 5.60607 1 12 1Z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  />
                                </svg>
                              ) : (
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  />
                                  <circle
                                    cx="12"
                                    cy="12"
                                    r="3"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  />
                                </svg>
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </InputContainer>
                </PasswordSection>
              </Box>
            </FormContent>

            {error && (
              <Typography
                color="error"
                variant="body2"
                sx={{ mt: -2, textAlign: "center" }}
              >
                {error}
              </Typography>
            )}

            {success && (
              <Typography
                color="success.main"
                variant="body2"
                sx={{ mt: -2, textAlign: "center" }}
              >
                {success}
              </Typography>
            )}

            <ContinueButton
              type="submit"
              disabled={loading || !isFormValid}
              isActive={isFormValid}
              onClick={onSubmit}
            >
              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <CircularProgress size={20} color="inherit" />
                  RESETTING...
                </Box>
              ) : (
                "CONTINUE"
              )}
            </ContinueButton>
          </FormContainer>
        </RightPanel>
      </ContactSection>
    </SignUpContainer>
  );
}
